/**
 * Regroupe les questions d'une évaluation en blocs pour l'affichage print.
 *
 * Trois règles de regroupement appliquées dans l'ordre :
 *
 *   0. **Figure d'énoncé partagée** (PRIORITAIRE) → les sélections
 *      consécutives dont l'énoncé porte une figure STRICTEMENT identique
 *      (image data-URL, droite graduée, scène compas/rapporteur, geogebra —
 *      égalité JSON de l'élément) sont regroupées en un seul exercice, la
 *      figure affichée une fois (`figurePartagee` sur le bloc). Types
 *      homogènes → le kind de groupe existant ; types hétérogènes →
 *      'figure-group' (sous-questions a. b. c., consigne par item).
 *      Garde-fous : figure jamais référencée par `{{element:N}}` EN PLEIN
 *      TEXTE dans un item (orpheline ou en fin d'énoncé : hissable),
 *      image-porteuse exclue, types imprimables seulement.
 *
 *   1. **Même exerciseur** (tanoed) → toutes les sélections consécutives qui
 *      partagent `_meta.exerciseurKey` sont groupées, quel que soit le type
 *      (qcm, texte, V/F). Le `kind` du bloc dépend du type :
 *        - qcm standard                → 'qcm-group'
 *        - qcm avec table vraiFaux     → 'vf-group'
 *        - texte (saisie)              → 'saisie-group'
 *
 *   2. **Legacy QCM de la banque** (pas de `exerciseurKey`) → les QCM simples
 *      consécutifs sont groupés comme avant. Évite la régression sur les
 *      évaluations existantes qui piochent dans la banque.
 *
 * Un bloc groupé à 1 seul item est dégénéré en `single` (pas de wrapper de
 * groupe inutile).
 *
 * Numéro d'exercice (`exNum`) : index final dans la liste des blocs. Sera
 * éventuellement réécrit par `packBlocks.renumberBlocks` après placement.
 */

export const MAX_GROUPED_OPTIONS = 4;
/** Plafond de sous-questions dans un groupe (évite des blocs démesurés). */
export const MAX_GROUP_ITEMS = 10;

export const isVraiFaux = (payload) =>
    payload?.type === 'qcm'
    && payload?.elements_additionnels?.elements?.some(
        (e) => e?.type === 'table' && e.config?.type === 'vraiFaux'
    );

/**
 * Exercice-tableau générique (colonnesLibres…) : type 'qcm' véhicule, AUCUN
 * choix à entourer — la réponse se coche DANS le tableau. Doit suivre le même
 * routage que les V/F (PrintVraiFaux / vf-group), jamais PrintQcm dont la
 * consigne "Entoure…" n'a aucun sens ici. Un vrai QCM accompagné d'une table
 * informative (choix non vides) reste un QCM standard.
 */
export const isTableExercise = (payload) =>
    payload?.type === 'qcm'
    && !(payload?.contenu?.choix?.length > 0)
    && payload?.elements_additionnels?.elements?.some((e) => e?.type === 'table');

/**
 * Question EvalNat "image-porteuse" : la capture nationale contient l'énoncé
 * COMPLET (consigne + choix). Marquage posé par evalNatAdapter — jamais
 * présent sur les payloads banque/générés. Jamais groupée (un groupe QCM
 * imprimerait sa consigne "Entoure…" par-dessus), rendue image seule.
 */
export const estImagePorteuse = (payload) => payload?._meta?.imagePorteuse === true;

const countCorrect = (payload) => {
    const br = payload?.contenu?.bonne_reponse;
    if (br == null || br === '') return 0;
    if (Array.isArray(br)) return br.length;
    return 1;
};

/** QCM de la banque groupables entre eux (legacy, sans exerciseurKey). */
export const canBeGrouped = (payload) => {
    if (payload?.type !== 'qcm') return false;
    if (isVraiFaux(payload) || isTableExercise(payload) || estImagePorteuse(payload)) return false;
    if (!payload?.contenu?.multi) return true;
    return countCorrect(payload) <= MAX_GROUPED_OPTIONS;
};

/** Détermine le `kind` d'un bloc groupé à partir du type du 1er payload. */
const groupKindOf = (payload) => {
    // Image-porteuse : jamais de groupe → null force le rendu single
    if (estImagePorteuse(payload)) return null;
    if (isVraiFaux(payload) || isTableExercise(payload)) return 'vf-group';
    if (payload?.type === 'qcm') return 'qcm-group';
    if (payload?.type === 'texte') return 'saisie-group';
    // Types non supportés en groupé → force en single (retour null)
    return null;
};

// ─── Règle 0 : figure d'énoncé partagée ────────────────────────────────

/** Types d'éléments d'énoncé éligibles au partage (le tableau est un support
 *  de réponse, l'expression fait partie du texte : exclus). */
const TYPES_FIGURE = new Set(['image', 'numberline', 'compas_interactif', 'rapporteur_interactif', 'geogebra']);

/** Sérialisation à CLÉS TRIÉES : l'ordre des clés d'un JSON n'est pas
 *  significatif (deux injections en base peuvent l'écrire différemment),
 *  seule l'égalité de CONTENU compte. */
const stableStringify = (v) => {
    if (v === null || typeof v !== 'object') return JSON.stringify(v);
    if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`;
    return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${stableStringify(v[k])}`).join(',')}}`;
};

/** Clé d'identité STRICTE d'une figure (égalité de contenu JSON, ordre des
 *  clés neutralisé), null si inéligible. */
export const cleFigure = (el) => {
    if (!el || !TYPES_FIGURE.has(el.type)) return null;
    try { return stableStringify(el); } catch { return null; }
};

/** Index de la figure partagée dans les éléments d'UN payload (−1 si absente).
 *  Sert aux renderers pour la skipper de l'énoncé de chaque sous-question. */
export const indexFigurePartagee = (payload, cle) => {
    if (!cle) return -1;
    const els = payload?.elements_additionnels?.elements || [];
    return els.findIndex((el) => cleFigure(el) === cle);
};

const PLACEHOLDER_FIG_RE = /\{\{element:(\d+)\}\}/g;

/** Queue « inerte » après un placeholder : espaces, balises, autres
 *  placeholders — rien de textuel. */
const QUEUE_INERTE_RE = /^(\s|<[^>]*>|&nbsp;)*$/i;

/**
 * Indices d'éléments NON hissables : référencés par `{{element:N}}` suivi de
 * contenu textuel (les retirer laisserait la phrase en suspens). Un
 * placeholder TERMINAL (fin d'énoncé, éventuellement suivi de balises
 * fermantes / espaces / autres placeholders) reste hissable — c'est le
 * motif standard des générateurs (`…dans le repère.</p>{{element:0}}`).
 */
const indicesNonHissables = (texte) => {
    const refs = new Set();
    if (typeof texte !== 'string') return refs;
    PLACEHOLDER_FIG_RE.lastIndex = 0;
    let m;
    while ((m = PLACEHOLDER_FIG_RE.exec(texte)) !== null) {
        const queue = texte
            .slice(m.index + m[0].length)
            .replace(/\{\{element:\d+\}\}/g, '');
        if (!QUEUE_INERTE_RE.test(queue)) refs.add(parseInt(m[1], 10));
    }
    return refs;
};

/** Types que le print sait rendre en sous-question (cf. PrintExo) — un type
 *  inconnu (placeholder « prochaine version ») ne rejoint jamais un groupe. */
const SUBTYPES_ASSOCIATION_PRINT = new Set([
    'relier', 'ordonner', 'classer', 'texte_trou',
    'placer_points', 'placer_points_2d', 'programme_construction', 'placer_aiguilles',
]);
const typeImprimableEnGroupe = (payload) => {
    if (estImagePorteuse(payload)) return false;
    if (payload?.type === 'qcm' || payload?.type === 'texte') return true;
    return payload?.type === 'association'
        && SUBTYPES_ASSOCIATION_PRINT.has(payload?.contenu?.subType);
};

/** Clés des figures HISSABLES d'un payload : éléments éligibles, orphelins
 *  ou référencés en position terminale (cf. indicesNonHissables — seule une
 *  figure référencée EN PLEIN TEXTE est exclue). Map clé → index.
 *
 *  Un énoncé COMPOSÉ de plusieurs figures (gen_1050 Série flash : quatre
 *  droites graduées A, B, C, D) n'est JAMAIS candidat au hissage (revue prof
 *  2026-09-08) : un run ne retenait qu'UNE figure commune, la droite A
 *  identique de deux séries consécutives était hissée au-dessus avec des
 *  sous-questions a., b., les trois autres restant dans l'énoncé. Le bloc
 *  « figure partagée » ne sait afficher qu'une figure ; une composition
 *  garde donc son énoncé entier et ne rejoint que les groupes des règles 1
 *  et 2. */
const figuresHissables = (payload) => {
    const m = new Map();
    if (!typeImprimableEnGroupe(payload)) return m;
    const els = payload?.elements_additionnels?.elements;
    if (!Array.isArray(els) || !els.length) return m;
    if (els.filter((el) => cleFigure(el) !== null).length > 1) return m;
    const refs = indicesNonHissables(payload?.texte);
    els.forEach((el, idx) => {
        if (refs.has(idx)) return;
        const cle = cleFigure(el);
        if (cle && !m.has(cle)) m.set(cle, idx);
    });
    return m;
};

/** Runs consécutifs (≥ 2 items, ≤ MAX_GROUP_ITEMS) partageant au moins une
 *  figure commune à TOUS les items. Retourne [{start, end, cle}]. */
const detecterRunsFigure = (questions) => {
    const runs = [];
    let i = 0;
    while (i < questions.length) {
        const cles = figuresHissables(questions[i]);
        if (!cles.size) { i += 1; continue; }
        let inter = [...cles.keys()];
        let j = i;
        while (j + 1 < questions.length && (j + 1 - i) < MAX_GROUP_ITEMS) {
            const next = figuresHissables(questions[j + 1]);
            const nextInter = inter.filter((k) => next.has(k));
            if (!nextInter.length) break;
            inter = nextInter;
            j += 1;
        }
        if (j > i) {
            runs.push({ start: i, end: j, cle: inter[0] });
            i = j + 1;
        } else {
            i += 1;
        }
    }
    return runs;
};

/** Bloc d'un run : kind homogène si tous les items partagent le même kind de
 *  groupe (aspect actuel + figure hissée), sinon 'figure-group' mixte. */
const buildRunBlock = (run, questions) => {
    const items = [];
    for (let k = run.start; k <= run.end; k += 1) {
        items.push({ payload: questions[k], originalIndex: k });
    }
    const kinds = items.map(({ payload }) => groupKindOf(payload));
    const homogene = kinds[0] != null && kinds.every((k) => k === kinds[0]);
    return {
        kind: homogene ? kinds[0] : 'figure-group',
        items,
        figurePartagee: { cle: run.cle, element: JSON.parse(run.cle) },
    };
};

/** Le bloc courant peut-il accueillir le payload suivant ? */
const canExtendGroup = (group, firstPayload, nextPayload) => {
    if (!group) return false;
    if (group.items.length >= MAX_GROUP_ITEMS) return false;

    const prevKey = firstPayload?._meta?.exerciseurKey;
    const nextKey = nextPayload?._meta?.exerciseurKey;

    // Règle 1 : même exerciseurKey → groupe n'importe quel type compatible
    // (multi-sélections d'un même exerciseur tanoed)
    if (prevKey && prevKey === nextKey) {
        return groupKindOf(nextPayload) === group.kind;
    }

    // Règle 2 : QCM groupables entre eux quelle que soit la source
    // (exos/formats différents, banque + tanoed mixés).
    // Respecte canBeGrouped (QCM multi > 4 bonnes rép. restent standalone).
    if (group.kind === 'qcm-group' && canBeGrouped(nextPayload)) {
        return true;
    }

    return false;
};

export const groupQuestions = (questions = []) => {
    // Règle 0 (prioritaire) : runs consécutifs à figure d'énoncé identique.
    // Un run consommé est une frontière : les règles 1/2 ne groupent jamais
    // à travers.
    const runAt = new Map(detecterRunsFigure(questions).map((r) => [r.start, r]));

    const blocks = [];
    let currentGroup = null;
    let currentFirstPayload = null;

    for (let i = 0; i < questions.length; i += 1) {
        const run = runAt.get(i);
        if (run) {
            blocks.push(buildRunBlock(run, questions));
            currentGroup = null;
            currentFirstPayload = null;
            i = run.end;
            continue;
        }

        const payload = questions[i];
        const originalIndex = i;
        const exKey = payload?._meta?.exerciseurKey;

        // Tentative d'extension du groupe courant
        if (canExtendGroup(currentGroup, currentFirstPayload, payload)) {
            currentGroup.items.push({ payload, originalIndex });
            continue;
        }

        // Démarre un nouveau groupe tanoed (même exerciseurKey)
        if (exKey) {
            const kind = groupKindOf(payload);
            if (kind) {
                currentGroup = { kind, items: [{ payload, originalIndex }] };
                currentFirstPayload = payload;
                blocks.push(currentGroup);
                continue;
            }
            // Type non groupable → single
        }

        // Démarre un groupe QCM bank legacy
        if (!exKey && canBeGrouped(payload)) {
            currentGroup = { kind: 'qcm-group', items: [{ payload, originalIndex }] };
            currentFirstPayload = payload;
            blocks.push(currentGroup);
            continue;
        }

        // Fallback : single
        currentGroup = null;
        currentFirstPayload = null;
        blocks.push({ kind: 'single', payload, originalIndex });
    }

    // Post-processing : groupe à 1 item → single (pas de wrapper inutile)
    const collapsed = blocks.map((b) => {
        if (
            (b.kind === 'qcm-group' || b.kind === 'saisie-group' || b.kind === 'vf-group'
                || b.kind === 'figure-group')
            && b.items.length === 1
        ) {
            const { payload, originalIndex } = b.items[0];
            return { kind: 'single', payload, originalIndex };
        }
        return b;
    });

    // `uid` : identité STABLE du bloc, ancrée sur l'originalIndex du 1er item —
    // survit aux recompositions et sert de clé aux réglages prof (zoom manuel,
    // densité forcée). Les sous-blocs issus d'un split reçoivent `uid_a`/`uid_b`
    // et NE possèdent aucun réglage : la résolution remonte la chaîne de
    // suffixes jusqu'à l'uid parent (cf. resolveUidSetting, packBlocks.js).
    return collapsed.map((b, idx) => ({
        ...b,
        exNum: idx + 1,
        uid: b.kind === 'single' ? `s${b.originalIndex}` : `g${b.items[0].originalIndex}`,
    }));
};
